import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import { Alert, AlertTitle, AlertDescription } from 'components/ui/alert';
import { CityAutocomplete } from 'components/CityAutocomplete';
import { CountryAutocomplete } from 'components/CountryAutocomplete';
import { TrackingEmailField } from 'components/TrackingEmailField';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { PARCEL_TYPES } from 'lib/format';
import { AlertTriangle, ImagePlus, Loader2, Package, Plus, Trash2, X } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';

const emptyExtraParcel = () => ({ title: '', weight_kg: 1, size: 'S', budget: 20 });
const MAX_PHOTOS = 5;
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 1200;
const MAX_PHOTO_DATA_URL_LENGTH = 450000;

const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_DECODE_ERROR'));
    image.src = String(reader.result || '');
  };
  reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
  reader.readAsDataURL(file);
});

const compressParcelPhoto = async (file) => {
  const image = await loadImage(file);
  let ratio = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('CANVAS_UNAVAILABLE');

  for (let pass = 0; pass < 5; pass += 1) {
    const width = Math.max(1, Math.round(image.width * ratio));
    const height = Math.max(1, Math.round(image.height * ratio));
    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    for (let quality = 0.84; quality >= 0.44; quality -= 0.08) {
      const output = canvas.toDataURL('image/jpeg', quality);
      if (output.length <= MAX_PHOTO_DATA_URL_LENGTH) return output;
    }
    ratio *= 0.8;
  }

  throw new Error('IMAGE_TOO_LARGE');
};

export default function CreateDeliveryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [extraParcels, setExtraParcels] = useState([]);
  const [photosProcessing, setPhotosProcessing] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', parcel_type: 'small',
    weight_kg: 1, size: 'S', dimensions_cm: '', estimated_value: 0,
    contents: '', departure_country: '', departure_city: '',
    departure_lat: null, departure_lng: null, departure_address: '', arrival_country: '', arrival_city: '', arrival_lat: null, arrival_lng: null, arrival_address: '',
    recipient_name: '', recipient_phone: '', recipient_email: '',
    desired_date: '',
    budget: 20, fragile: false, urgent: false, customs_declaration: '', customs_declared: false, photos: [],
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isInternational = form.departure_country && form.arrival_country && form.departure_country !== form.arrival_country;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const basePayload = {
        ...form,
        recipient_email: form.recipient_email.trim() || null,
        weight_kg: Number(form.weight_kg),
        estimated_value: Number(form.estimated_value),
        budget: Number(form.budget),
      };
      if (extraParcels.length === 0) {
        await api.post('/deliveries', basePayload);
        toast.success(t('delivery.published'));
      } else {
        const batch = [
          basePayload,
          ...extraParcels.map((parcel) => ({
            ...basePayload,
            title: parcel.title || basePayload.title,
            weight_kg: Number(parcel.weight_kg),
            size: parcel.size,
            budget: Number(parcel.budget),
          })),
        ];
        await api.post('/deliveries/batch', batch);
        toast.success(`${batch.length} ${t('delivery.batchPublished')}`);
      }
      navigate('/deliveries');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('delivery.error')));
    } finally {
      setLoading(false);
    }
  };

  const addExtraParcel = () => setExtraParcels((prev) => [...prev, emptyExtraParcel()]);
  const updateExtraParcel = (index, key, value) => setExtraParcels((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)));
  const removeExtraParcel = (index) => setExtraParcels((prev) => prev.filter((_, i) => i !== index));

  const addPhotos = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    const remaining = MAX_PHOTOS - form.photos.length;
    if (!remaining || !selectedFiles.length) return;
    if (selectedFiles.length > remaining) toast.error(t('delivery.photosMax'));

    setPhotosProcessing(true);
    try {
      const validFiles = selectedFiles.slice(0, remaining);
      if (validFiles.some((file) => !file.type.startsWith('image/') || file.size > MAX_UPLOAD_BYTES)) {
        toast.error(t('delivery.photoSize'));
      }
      const compressedPhotos = await Promise.all(
        validFiles
          .filter((file) => file.type.startsWith('image/') && file.size <= MAX_UPLOAD_BYTES)
          .map(compressParcelPhoto),
      );
      set('photos', [...form.photos, ...compressedPhotos]);
    } catch (error) {
      toast.error(error.message === 'IMAGE_TOO_LARGE' ? t('delivery.photoTooLarge') : t('delivery.photoError'));
    } finally {
      setPhotosProcessing(false);
    }
  };

  const removePhoto = (index) => set('photos', form.photos.filter((_, photoIndex) => photoIndex !== index));

  return (
    <DashboardShell>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Package className="text-primary" /> {t('delivery.createTitle')}
        </h1>
        <p className="text-muted-foreground">{t('delivery.createSubtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
        {!user?.is_verified_id && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('delivery.verifyTitle')}</AlertTitle>
            <AlertDescription>{t('delivery.verifyText')}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader><CardTitle className="text-base">{t('delivery.details')}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>{t('delivery.title')}</Label>
              <Input required value={form.title} onChange={e => set('title', e.target.value)} placeholder={t('delivery.titlePlaceholder')} data-testid="delivery-title-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('delivery.description')}</Label>
              <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder={t('delivery.descriptionPlaceholder')} data-testid="delivery-description-input" />
            </div>
            <div>
              <Label>{t('delivery.type')}</Label>
              <Select value={form.parcel_type} onValueChange={v => set('parcel_type', v)}>
                <SelectTrigger data-testid="delivery-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PARCEL_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('delivery.size')}</Label>
              <Select value={form.size} onValueChange={v => set('size', v)}>
                <SelectTrigger data-testid="delivery-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="S">{t('delivery.sizeS')}</SelectItem>
                  <SelectItem value="M">{t('delivery.sizeM')}</SelectItem>
                  <SelectItem value="L">{t('delivery.sizeL')}</SelectItem>
                  <SelectItem value="XL">{t('delivery.sizeXL')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('delivery.weight')}</Label>
              <Input type="number" min={0.1} step={0.1} required value={form.weight_kg} onChange={e => set('weight_kg', e.target.value)} data-testid="delivery-weight-input" />
            </div>
            <div>
              <Label>{t('delivery.dimensions')}</Label>
              <Input value={form.dimensions_cm} onChange={e => set('dimensions_cm', e.target.value)} placeholder="30x20x10" />
            </div>
            <div>
              <Label>{t('delivery.estimatedValue')}</Label>
              <Input type="number" min={0} value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} />
            </div>
            <div>
              <Label>{t('delivery.contents')}</Label>
              <Input value={form.contents} onChange={e => set('contents', e.target.value)} placeholder={t('delivery.contentsPlaceholder')} />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="delivery-photos">{t('delivery.photos')}</Label>
              <div className="flex flex-wrap items-center gap-3">
                {form.photos.map((photo, index) => (
                  <div key={photo} className="relative h-24 w-24 overflow-hidden rounded-md border bg-muted">
                    <img src={photo} alt={`${t('delivery.photos')} ${index + 1}`} className="h-full w-full object-cover" />
                    <Button type="button" size="icon" variant="destructive" className="absolute right-1 top-1 h-6 w-6" onClick={() => removePhoto(index)} aria-label={`${t('delivery.removePhoto')} ${index + 1}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                ))}
                {form.photos.length < MAX_PHOTOS && (
                  <label htmlFor="delivery-photos" className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed text-muted-foreground hover:border-primary hover:text-primary">
                    <ImagePlus size={22} />
                    <span className="text-xs">{t('delivery.add')}</span>
                  </label>
                )}
              </div>
              <Input id="delivery-photos" type="file" accept="image/*" multiple className="sr-only" onChange={addPhotos} disabled={photosProcessing || form.photos.length >= MAX_PHOTOS} data-testid="delivery-photos-input" />
              <p className="text-xs text-muted-foreground">{t('delivery.photosLimit')}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{t('delivery.extraParcels')}</CardTitle>
              <Button type="button" size="sm" variant="outline" onClick={addExtraParcel}><Plus size={14} className="mr-1" /> {t('delivery.addParcel')}</Button>
            </div>
          </CardHeader>
          {extraParcels.length > 0 && (
            <CardContent className="space-y-3">
              {extraParcels.map((parcel, index) => (
                <div key={index} className="grid sm:grid-cols-4 gap-2 items-end border-t pt-3 first:border-0 first:pt-0">
                  <div className="sm:col-span-2">
                    <Label>{t('delivery.parcelTitle')}</Label>
                    <Input value={parcel.title} onChange={e => updateExtraParcel(index, 'title', e.target.value)} placeholder={form.title || t('delivery.extraParcels')} />
                  </div>
                  <div>
                    <Label>{t('delivery.weight')}</Label>
                    <Input type="number" min={0.1} step={0.1} value={parcel.weight_kg} onChange={e => updateExtraParcel(index, 'weight_kg', e.target.value)} />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label>{t('delivery.budget')}</Label>
                      <Input type="number" min={1} value={parcel.budget} onChange={e => updateExtraParcel(index, 'budget', e.target.value)} />
                    </div>
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeExtraParcel(index)}><X size={16} /></Button>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">{t('delivery.route')}</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t('delivery.departureCountry')}</Label>
              <CountryAutocomplete
                required
                value={form.departure_country}
                onChange={value => set('departure_country', value)}
                onSelect={country => setForm(current => ({ ...current, departure_country: country, departure_city: '', departure_lat: null, departure_lng: null }))}
                testId="delivery-departure-country-input"
              />
            </div>
            <div>
              <Label>{t('delivery.departureCity')}</Label>
              <CityAutocomplete
                required
                value={form.departure_city}
                country={form.departure_country}
                onChange={value => set('departure_city', value)}
                onSelect={({ city, country, latitude, longitude }) => setForm(current => ({ ...current, departure_city: city, departure_country: country, departure_lat: latitude, departure_lng: longitude }))}
                placeholder="Paris"
                testId="delivery-departure-city-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('delivery.departureAddress')}</Label>
              <Input value={form.departure_address} onChange={e => set('departure_address', e.target.value)} placeholder={t('delivery.addressPlaceholder')} data-testid="delivery-departure-address-input" />
            </div>
            <div>
              <Label>{t('delivery.arrivalCountry')}</Label>
              <CountryAutocomplete
                required
                value={form.arrival_country}
                onChange={value => set('arrival_country', value)}
                onSelect={country => setForm(current => ({ ...current, arrival_country: country, arrival_city: '', arrival_lat: null, arrival_lng: null }))}
                placeholder={t('delivery.countryPlaceholder')}
                testId="delivery-arrival-country-input"
              />
            </div>
            <div>
              <Label>{t('delivery.arrivalCity')}</Label>
              <CityAutocomplete
                required
                value={form.arrival_city}
                country={form.arrival_country}
                onChange={value => set('arrival_city', value)}
                onSelect={({ city, country, latitude, longitude }) => setForm(current => ({ ...current, arrival_city: city, arrival_country: country, arrival_lat: latitude, arrival_lng: longitude }))}
                placeholder="Conakry"
                testId="delivery-arrival-city-input"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>{t('delivery.arrivalAddress')}</Label>
              <Input value={form.arrival_address} onChange={e => set('arrival_address', e.target.value)} placeholder={t('delivery.addressPlaceholder')} data-testid="delivery-arrival-address-input" />
            </div>
            <div>
              <Label>{t('delivery.recipientName')}</Label>
              <Input value={form.recipient_name} onChange={e => set('recipient_name', e.target.value)} />
            </div>
            <div>
              <Label>{t('delivery.recipientPhone')}</Label>
              <Input value={form.recipient_phone} onChange={e => set('recipient_phone', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <TrackingEmailField accountEmail={user?.email} value={form.recipient_email} onChange={value => set('recipient_email', value)} />
            </div>
            <div>
              <Label>{t('delivery.desiredDate')}</Label>
              <Input type="date" value={form.desired_date} onChange={e => set('desired_date', e.target.value)} data-testid="delivery-date-input" />
            </div>
            <div>
              <Label>{t('delivery.budget')} *</Label>
              <Input type="number" min={1} required value={form.budget} onChange={e => set('budget', e.target.value)} data-testid="delivery-budget-input" />
            </div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <Checkbox id="fragile" checked={form.fragile} onCheckedChange={v => set('fragile', v)} />
              <Label htmlFor="fragile" className="cursor-pointer">{t('delivery.fragile')}</Label>
              <Checkbox id="urgent" className="ml-4" checked={form.urgent} onCheckedChange={v => set('urgent', v)} />
              <Label htmlFor="urgent" className="cursor-pointer">{t('delivery.urgent')}</Label>
            </div>
          </CardContent>
        </Card>

        {isInternational && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('delivery.international')}</AlertTitle>
            <AlertDescription>{t('delivery.customsWarning')}</AlertDescription>
            <div className="mt-4 space-y-3">
              <div>
                <Label htmlFor="customs-declaration">{t('delivery.customsDeclaration')}</Label>
                <Textarea id="customs-declaration" value={form.customs_declaration} onChange={e => set('customs_declaration', e.target.value)} placeholder={t('delivery.customsPlaceholder')} required />
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="customs-declared" checked={form.customs_declared} onCheckedChange={v => set('customs_declared', v === true)} />
                <Label htmlFor="customs-declared" className="cursor-pointer leading-5">{t('delivery.customsConfirm')}</Label>
              </div>
            </div>
          </Alert>
        )}

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>{t('delivery.cancel')}</Button>
          <Button type="submit" disabled={loading || !user?.is_verified_id || (isInternational && (!form.customs_declared || !form.customs_declaration.trim()))} data-testid="delivery-submit-button">
            {loading ? <Loader2 className="animate-spin" size={16} /> : (extraParcels.length > 0 ? `${t('delivery.publish')} (${extraParcels.length + 1})` : t('delivery.publish'))}
          </Button>
        </div>
      </form>
    </DashboardShell>
  );
}
